import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { PinKeypad } from '@/components/auth/PinKeypad';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { KeyRound, ShieldCheck, CheckCircle2 } from 'lucide-react';

export default function ChangePinPage() {
    const { refresh, setMustChangePin } = useAuth();
    const nav = useNavigate();
    const [step, setStep] = useState(1); // 1 = new pin, 2 = confirm
    const [newPin, setNewPin] = useState('');
    const [err, setErr] = useState('');
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const handleNewPin = (p) => {
        if (p === '9999') {
            setErr('El PIN no puede ser 9999. Elige otro.');
            return;
        }
        setNewPin(p);
        setErr('');
        setStep(2);
    };

    const handleConfirmPin = async (p) => {
        if (p !== newPin) {
            setErr('Los PIN no coinciden. Elige uno nuevo.');
            setStep(1);
            setNewPin('');
            return;
        }
        setErr('');
        setBusy(true);
        try {
            await api.post('/auth/change-pin', { current_pin: '9999', new_pin: newPin });
            setDone(true);
            toast.success('¡PIN configurado con éxito!');
            setMustChangePin(false);
            await refresh();
            setTimeout(() => nav('/dashboard', { replace: true }), 1600);
        } catch (e) {
            const msg = e?.response?.data?.detail || 'Error al guardar el PIN';
            setErr(msg);
            setStep(1);
            setNewPin('');
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-red-950 to-slate-900 px-4 py-10">
            {/* Logo */}
            <div className="mb-10">
                <img
                    src="/assets/oklovendo-logo.png"
                    alt="Ok te lo Vendo"
                    className="h-10 w-auto"
                    style={{ filter: 'brightness(0) invert(1)' }}
                />
            </div>

            {done ? (
                /* ── Success state ── */
                <div className="w-full max-w-xs rounded-3xl bg-white/10 backdrop-blur border border-white/20 p-8 text-center text-white animate-in fade-in zoom-in duration-300">
                    <CheckCircle2 className="h-20 w-20 text-emerald-400 mx-auto mb-4 drop-shadow-lg" />
                    <div className="text-2xl font-bold">¡PIN Configurado!</div>
                    <p className="text-sm text-white/60 mt-2">Redirigiendo a tu dashboard…</p>
                </div>
            ) : (
                <div className="w-full max-w-xs">
                    {/* Step dots */}
                    <div className="flex items-center justify-center gap-2 mb-8">
                        <div className={`h-2 rounded-full transition-all duration-300 ${step === 1 ? 'w-10 bg-red-400' : 'w-3 bg-white/25'}`} />
                        <div className={`h-2 rounded-full transition-all duration-300 ${step === 2 ? 'w-10 bg-red-400' : 'w-3 bg-white/25'}`} />
                    </div>

                    <div className="rounded-3xl bg-white/10 backdrop-blur-md border border-white/15 overflow-hidden shadow-2xl">
                        {/* Header */}
                        <div className="bg-red-600/20 border-b border-white/10 px-6 py-5 flex flex-col items-center gap-3">
                            <div className="h-14 w-14 rounded-2xl bg-red-600/40 border border-red-400/30 flex items-center justify-center shadow-lg">
                                {step === 1
                                    ? <KeyRound className="h-7 w-7 text-red-200" />
                                    : <ShieldCheck className="h-7 w-7 text-red-200" />
                                }
                            </div>
                            <div className="text-center">
                                <h1 className="text-lg font-bold text-white">
                                    {step === 1 ? 'Crea tu PIN de acceso' : 'Confirma tu PIN'}
                                </h1>
                                <p className="text-xs text-white/55 mt-1 leading-relaxed">
                                    {step === 1
                                        ? 'Elige 4 dígitos que recuerdes fácilmente'
                                        : 'Ingresa los mismos 4 dígitos para confirmar'}
                                </p>
                            </div>
                        </div>

                        {/* Body */}
                        <div className="px-6 py-6">
                            {err && (
                                <div className="mb-5 rounded-xl bg-red-500/25 border border-red-400/40 text-red-200 text-sm text-center px-4 py-3 leading-snug">
                                    {err}
                                </div>
                            )}

                            {step === 1 && (
                                <PinKeypad
                                    key="step-new"
                                    onChange={() => {}}
                                    onComplete={handleNewPin}
                                />
                            )}
                            {step === 2 && (
                                <PinKeypad
                                    key="step-confirm"
                                    onChange={() => {}}
                                    onComplete={handleConfirmPin}
                                />
                            )}

                            {busy && (
                                <div className="mt-5 text-center text-sm text-white/50 animate-pulse">
                                    Guardando PIN…
                                </div>
                            )}
                        </div>
                    </div>

                    {step === 2 && !busy && (
                        <button
                            onClick={() => { setStep(1); setNewPin(''); setErr(''); }}
                            className="mt-4 w-full text-sm text-white/35 hover:text-white/65 transition-colors text-center py-2">
                            ← Elegir otro PIN
                        </button>
                    )}
                </div>
            )}

            <p className="mt-10 text-xs text-white/25 text-center max-w-xs">
                Tu PIN es personal y confidencial. No lo compartas con nadie.
            </p>
        </div>
    );
}
